import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Grid3x3, RotateCcw } from "lucide-react";
import { useDateRange } from "@/components/DateRangeFilter";
import { Annotation } from "@/components/Annotation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return Math.round(val);
};

export default function CohortAnalysis() {
  const [data, setData] = useState([]);
  const [retention, setRetention] = useState([]);
  const [metric, setMetric] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tab, setTab] = useState("heatmap");
  const { filterData } = useDateRange();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cohort/acquisition`),
      axios.get(`${API}/cohort/retention`)
    ]).then(([cohortRes, retRes]) => {
      setData(cohortRes.data.data || []);
      setRetention(retRes.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filteredData = useMemo(() => filterData(data, "cohort"), [data, filterData]);
  const filteredRetention = useMemo(() => filterData(retention, "cohort"), [retention, filterData]);

  const maxOffset = 12;

  // Compute min/max for color scaling
  const { minVal, maxVal } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    filteredData.forEach((c) => {
      Object.entries(c.months || {}).forEach(([offset, vals]) => {
        if (parseInt(offset) > maxOffset) return;
        const v = metric === "revenue" ? vals.revenue : vals.customers;
        if (v > 0) {
          min = Math.min(min, v);
          max = Math.max(max, v);
        }
      });
    });
    return { minVal: min === Infinity ? 0 : min, maxVal: max === -Infinity ? 1 : max };
  }, [filteredData, metric]);

  const getColor = (val) => {
    if (!val || val <= 0) return "transparent";
    const ratio = Math.log(val + 1) / Math.log(maxVal + 1);
    const clamped = Math.min(Math.max(ratio, 0), 1);
    // Teal gradient: from dark (#042f2e) to bright (#10b981)
    const r = Math.round(4 + clamped * 12);
    const g = Math.round(47 + clamped * 138);
    const b = Math.round(46 + clamped * 83);
    return `rgb(${r}, ${g}, ${b})`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="h-[400px] skeleton-shimmer rounded" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="cohort-analysis-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
              Acquisition Cohort Analysis
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Customers grouped by first purchase month &middot; {filteredData.length} cohorts
            </p>
          </div>
          <Select value={metric} onValueChange={setMetric} data-testid="metric-select">
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="metric-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="customers">Customers</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Cohorts", value: filteredData.length },
            { label: "Largest Cohort", value: filteredData.length > 0 ? Math.max(...filteredData.map(d => d.cohort_size)).toLocaleString() : 0 },
            { label: "Avg Cohort Size", value: filteredData.length > 0 ? Math.round(filteredData.reduce((s, d) => s + d.cohort_size, 0) / filteredData.length).toLocaleString() : 0 },
            { label: "Active Offsets", value: maxOffset + 1 },
          ].map((m) => (
            <div key={m.label} className="glass-card rounded-md p-4" data-testid={`cohort-metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">{m.label}</p>
              <p className="font-mono text-lg text-slate-200 mt-1">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Heatmap */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl overflow-hidden" data-testid="cohort-heatmap">
          <CardHeader className="pb-3">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <Grid3x3 className="w-4 h-4 text-emerald-400" />
              Cohort {metric === "revenue" ? "Revenue" : "Customer"} Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto pb-6">
            <div className="min-w-[800px]">
              {/* Header Row */}
              <div className="grid gap-px mb-px" style={{ gridTemplateColumns: `140px repeat(${maxOffset + 1}, 1fr)` }}>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2 font-semibold">Cohort</div>
                {Array.from({ length: maxOffset + 1 }, (_, i) => (
                  <div key={i} className="text-[10px] text-slate-500 text-center py-2 font-mono">M{i}</div>
                ))}
              </div>

              {/* Data Rows */}
              {filteredData.map((cohort) => (
                <div
                  key={cohort.cohort}
                  className="grid gap-px mb-px"
                  style={{ gridTemplateColumns: `140px repeat(${maxOffset + 1}, 1fr)` }}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <span className="text-xs text-slate-300 font-mono">{cohort.cohort}</span>
                    <span className="text-[10px] text-slate-500">({cohort.cohort_size})</span>
                  </div>
                  {Array.from({ length: maxOffset + 1 }, (_, i) => {
                    const vals = cohort.months?.[String(i)];
                    const val = vals ? (metric === "revenue" ? vals.revenue : vals.customers) : 0;
                    const isHovered = hoveredCell?.cohort === cohort.cohort && hoveredCell?.offset === i;

                    return (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <div
                            className={`heatmap-cell flex items-center justify-center rounded-sm py-1.5 text-[10px] font-mono ${
                              val > 0 ? "text-white/90" : "text-transparent"
                            }`}
                            style={{ backgroundColor: getColor(val) }}
                            onMouseEnter={() => setHoveredCell({ cohort: cohort.cohort, offset: i })}
                            onMouseLeave={() => setHoveredCell(null)}
                            data-testid={`heatmap-cell-${cohort.cohort}-${i}`}
                          >
                            {val > 0 ? (metric === "revenue" ? formatINR(val) : val) : ""}
                          </div>
                        </TooltipTrigger>
                        {vals && (
                          <TooltipContent className="bg-slate-900 border-slate-700 text-xs">
                            <p className="font-semibold text-slate-200">{cohort.cohort} &middot; Month {i}</p>
                            <p className="text-slate-400">Revenue: INR {vals.revenue?.toLocaleString()}</p>
                            <p className="text-slate-400">Customers: {vals.customers}</p>
                            <p className="text-slate-400">Orders: {vals.orders}</p>
                            <p className="text-slate-400">Rev/Customer: INR {vals.rev_per_customer?.toLocaleString()}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-800">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">Scale</span>
              <div className="flex items-center gap-0">
                {[0.1, 0.25, 0.4, 0.55, 0.7, 0.85, 1].map((r) => (
                  <div
                    key={r}
                    className="w-8 h-3 rounded-sm"
                    style={{ backgroundColor: getColor(Math.pow(maxVal, r)) }}
                  />
                ))}
              </div>
              <span className="text-[10px] text-slate-500">Low</span>
              <span className="text-[10px] text-slate-500 ml-auto">High</span>
            </div>
          </CardContent>
        </Card>

        <Annotation>
          Cohort revenue beyond M0 represents organic returning revenue at zero incremental acquisition cost. The heatmap patterns in M3–M6 align with the natural baby growth cycle — as children transition developmental stages, parents return for the next category of gear. Growing cohort sizes reflect improving acquisition efficiency. Important: these numbers only capture website-trackable purchases. Customers who buy their second product on Amazon (common during sale events when marketplace prices are lower) are not reflected here — making this a conservative view of true cohort value.
        </Annotation>

        {/* Cohort Size Table */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cohort-size-table">
          <CardHeader className="pb-3">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              Cohort Sizes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Cohort</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Size</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">M0 Revenue</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">M1+ Revenue</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Total Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.slice(0, 20).map((c) => {
                    const m0 = c.months?.["0"]?.revenue || 0;
                    const total = Object.values(c.months || {}).reduce((s, v) => s + (v.revenue || 0), 0);
                    return (
                      <tr key={c.cohort} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                        <td className="py-2.5 px-3 font-mono text-slate-300">{c.cohort}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-300">{c.cohort_size?.toLocaleString()}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-300">{formatINR(m0)}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-emerald-400">{formatINR(total - m0)}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-slate-200 font-medium">{formatINR(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Annotation>
          This table uses <strong className="text-amber-300">revenue-weighted contribution</strong> (not raw customer count) — a more meaningful investor metric showing actual revenue impact. Only mature cohorts (9+ months of observable data) are displayed. M0 = 100% baseline. Values in M1+ show additional revenue as a % of initial cohort revenue. Note: customers who create new accounts for first-time discount coupons appear as "new" in later cohorts rather than "returning" in their original cohort — meaning retention values here are structurally understated.
        </Annotation>

        {/* Revenue-Weighted Retention Table (Mature Cohorts Only) */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl overflow-hidden" data-testid="retention-table">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-400" />
                Revenue-Weighted Retention (Mature Cohorts, 9+ months)
              </CardTitle>
              <span className="text-[10px] text-slate-500 bg-slate-800/60 px-2 py-1 rounded font-mono">
                Revenue in M(n) as % of M0 Revenue
              </span>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto pb-6">
            <div className="min-w-[800px]">
              {/* Header */}
              <div className="grid gap-px mb-px" style={{ gridTemplateColumns: `160px repeat(13, 1fr)` }}>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2 font-semibold">Cohort</div>
                {Array.from({ length: 13 }, (_, i) => (
                  <div key={i} className="text-[10px] text-slate-500 text-center py-2 font-mono">M{i}</div>
                ))}
              </div>
              {/* Rows */}
              {filteredRetention.map((cohort) => (
                <div
                  key={cohort.cohort}
                  className="grid gap-px mb-px"
                  style={{ gridTemplateColumns: `160px repeat(13, 1fr)` }}
                >
                  <div className="flex items-center gap-1.5 px-2 py-1.5">
                    <span className="text-xs text-slate-300 font-mono">{cohort.cohort}</span>
                    <span className="text-[9px] text-slate-500">({cohort.cohort_size})</span>
                  </div>
                  {Array.from({ length: 13 }, (_, i) => {
                    const m = cohort.months?.[String(i)];
                    const revContrib = m?.rev_contribution || 0;
                    const bg = revContrib >= 100 ? "bg-emerald-600" :
                               revContrib >= 10 ? "bg-emerald-700/80" :
                               revContrib >= 5 ? "bg-emerald-800/60" :
                               revContrib >= 1 ? "bg-emerald-900/40" :
                               revContrib > 0 ? "bg-slate-800/60" : "bg-transparent";
                    return (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <div className={`heatmap-cell flex items-center justify-center rounded-sm py-1.5 text-[10px] font-mono ${revContrib > 0 ? "text-white/90" : "text-transparent"} ${bg}`}>
                            {revContrib > 0 ? `${revContrib}%` : ""}
                          </div>
                        </TooltipTrigger>
                        {m && (
                          <TooltipContent className="bg-slate-900 border-slate-700 text-xs">
                            <p className="font-semibold text-slate-200">{cohort.cohort} &middot; Month {i}</p>
                            <p className="text-slate-400">Revenue: INR {m.revenue?.toLocaleString()}</p>
                            <p className="text-slate-400">Revenue Contribution: {m.rev_contribution}% of M0</p>
                            <p className="text-slate-400">Customers: {m.customers} / {cohort.cohort_size} ({m.rate}%)</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
